///
/// EarthBrowser
///
/// Created by Matt Giger on 11/3/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Forecast cloud layer: syncs the `cloud_forecast` Arrow dataset
/// (published by radar-services) and hands its frames to the cloud
/// overlay, which takes over from the observations once the clock is
/// past the newest one:
///
///  - "ecmwf-cloud": global 3-hourly model-cloud frames built by
///    weather-services from ECMWF layered cloud cover; the overlay's
///    global field dissolves into them (CloudFieldBaker)
///  - "rrfs-ir13": NOAA RRFS simulated GOES band-13 over CONUS, hourly
///    from the four synoptic cycles; drawn through the overlay's
///    detail-region path on top of the global field
///
/// A sub-layer of Clouds: visible only while "cloud" is on too. Owns no
/// overlay of its own.
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type CloudsDatasetLayer } from "@/earthbrowser/dataset-distribution/clouds/CloudsDatasetLayer"
import { CloudForecastTextureFrame } from "@/earthbrowser/dataset-distribution/cloud-forecast/CloudForecastTextureFrame"
import { CloudTextureFrame } from "@/earthbrowser/dataset-distribution/clouds/CloudTextureFrame"
import { type RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { time, timedelta } from "@stovokor/math/time"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { cCloudForecastProductEcmwf, cCloudForecastProductIr13, CloudForecastTable, type CloudForecastFrameRecord } from "./CloudForecastTable"

// Frames valid earlier than this before now are left out (the forecast
// only draws past the newest observation; the cloud feed lags an hour or
// two, so keep a little more overlap than the radar does)
const cPastWindowMs = 4 * 3_600_000

/** Raw Arrow row shape for the forecast frames feed (only fields we read) */
interface CloudForecastRow {
	id: unknown
	product: unknown
	run_time: unknown
	valid_time: unknown
	forecast_minutes: unknown
	image_url: unknown
	source_path: unknown
	size_bytes: unknown
	width: unknown
	height: unknown
	vmin: unknown
	vmax: unknown
	bounds_wgs84: unknown
	created_at: unknown
}

/** "{-122.7,24.4,-72.3,47.8}" (Postgres array text) or JSON -> numbers */
function parseBounds(text: string): number[] | null {
	const values = text.replace(/[{}[\]\s]/g, "").split(",").map(Number)
	return values.length === 4 && values.every(Number.isFinite) ? values : null
}

export class CloudForecastDatasetLayer extends DatasetLayer<CloudForecastFrameRecord> {
	private cloudsLayer: CloudsDatasetLayer
	private frameCache = new Map<string, RadarTextureFrame>()
	private unsubscribe: (() => void) | null = null

	constructor(context: EarthBrowserContext, cloudsLayer: CloudsDatasetLayer) {
		super(context, "cloud_forecast")
		this.cloudsLayer = cloudsLayer
		this.cloudsLayer.overlay.forecastEnabled = () => this.visible

		const state = engineBridge.getState()
		this.visible = state ? CloudForecastDatasetLayer.wantsVisible(state.earthbrowser.visibleDatasets) : false

		this.unsubscribe = engineBridge.subscribe(() => {
			const current = engineBridge.getState()
			if (!current) return
			this.setVisible(CloudForecastDatasetLayer.wantsVisible(current.earthbrowser.visibleDatasets))
		})
	}

	/** Sub-layer of the clouds: both toggles must be on */
	private static wantsVisible(visibleDatasets: string[]): boolean {
		return visibleDatasets.includes("cloud") && visibleDatasets.includes("cloud_forecast")
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "cloud_forecast",
			pollIntervalMs: 5 * 60_000,
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await CloudForecastTable.create(db)
	}

	// The lane spans both series; the global one (longer, coarser) carries
	// the frame times unless only the regional frames are present
	private get laneSeries() {
		const overlay = this.cloudsLayer.overlay
		return overlay.globalForecast.timeRange ? overlay.globalForecast : overlay.forecast
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		if (!this.visible) return null
		const overlay = this.cloudsLayer.overlay
		const ranges = [overlay.globalForecast.timeRange, overlay.forecast.timeRange].filter(r => r !== undefined)
		if (ranges.length === 0) return null
		return {
			startMs: Math.min(...ranges.map(r => r.start.date.getTime())),
			endMs: Math.max(...ranges.map(r => r.end.date.getTime())),
		}
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		const series = this.laneSeries
		return {
			frameTimesMs: series.frameTimesMs(),
			loadedRanges: series.loadedRangesMs(),
			displayedFrameMs: series.displayedFrameTimeMs(),
		}
	}

	protected override frameLoadState(): LayerLoadState | null {
		// Only while the forecast is what's on screen
		const overlay = this.cloudsLayer.overlay
		if (overlay.forecastWeight <= 0) return null
		return overlay.globalForecast.loadState() ?? overlay.forecast.loadState()
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const rows = await downloadArrowRows(this.resolveUrl(shard.url, shard.path))
		await CloudForecastTable.replaceAll(this.parseRows(rows))
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const rows = await downloadArrowRows(this.resolveUrl(delta.upserts.url, delta.upserts.path))
		const upserts = this.parseRows(rows)
		if (upserts.length > 0) {
			await CloudForecastTable.upsert(upserts)
		}
	}

	private parseRows(rows: unknown[]): CloudForecastFrameRecord[] {
		return (rows as CloudForecastRow[]).map(r => ({
			id: String(r.id),
			product: String(r.product),
			run_time: new Date(String(r.run_time)).toISOString(),
			valid_time: new Date(String(r.valid_time)).toISOString(),
			forecast_minutes: Number(r.forecast_minutes),
			image_url: String(r.image_url),
			source_path: String(r.source_path),
			size_bytes: Number(r.size_bytes),
			width: Number(r.width),
			height: Number(r.height),
			vmin: Number(r.vmin),
			vmax: Number(r.vmax),
			bounds_wgs84: String(r.bounds_wgs84),
			created_at: new Date(String(r.created_at)).toISOString(),
		}))
	}

	public async queryFeatures(): Promise<CloudForecastFrameRecord[]> {
		return CloudForecastTable.getValidSince(new Date(Date.now() - cPastWindowMs).toISOString())
	}

	public createSceneObject(_feature: CloudForecastFrameRecord): DatasetSceneObject<CloudForecastFrameRecord> | undefined {
		return undefined
	}

	public async onSyncComplete(): Promise<void> {
		await this.refresh()
	}

	/** Re-read the frames from the database and hand them to the cloud overlay */
	public override async refresh(): Promise<void> {
		if (!this.visible) return
		try {
			const records = await this.queryFeatures()
			const wanted = new Set(records.map(r => r.image_url))
			for (const [url, frame] of this.frameCache) {
				if (!wanted.has(url)) {
					frame.purge()
					this.frameCache.delete(url)
				}
			}
			const regional: CloudForecastTextureFrame[] = []
			const global: RadarTextureFrame[] = []
			for (const record of records) {
				const cached = this.frameCache.get(record.image_url)
				if (record.product === cCloudForecastProductIr13) {
					let frame = cached instanceof CloudForecastTextureFrame ? cached : undefined
					if (!frame) {
						const bounds = parseBounds(record.bounds_wgs84)
						if (!bounds) continue
						frame = new CloudForecastTextureFrame(this.context, record, bounds, new time(record.valid_time), timedelta.hours(1))
						this.frameCache.set(record.image_url, frame)
					}
					regional.push(frame)
				} else if (record.product === cCloudForecastProductEcmwf) {
					// 3-hourly global equirect frames: the same class as the
					// observed hourly frames (wraps in longitude, mipmapped)
					let frame = cached
					if (!frame) {
						frame = new CloudTextureFrame(this.context, record.image_url, new time(record.valid_time), timedelta.hours(3))
						this.frameCache.set(record.image_url, frame)
					}
					global.push(frame)
				}
			}
			this.cloudsLayer.overlay.setForecastFrames(regional)
			this.cloudsLayer.overlay.setGlobalForecastFrames(global)
		} catch (error) {
			console.error("[CloudForecastDatasetLayer] Failed to refresh:", error)
		}
	}

	/** Dev instrumentation (window.__ebDebug.getCloudForecast) */
	public debugSnapshot(): Record<string, unknown> {
		const overlay = this.cloudsLayer.overlay
		const observedEnd = overlay.latestObservedMs
		return {
			visible: this.visible,
			weight: overlay.forecastWeight,
			observedEnd: observedEnd ? new Date(observedEnd).toISOString() : null,
			clock: this.context.clock.clockTime.date.toISOString(),
			regional: overlay.forecast.debugSnapshot(),
			global: overlay.globalForecast.debugSnapshot(),
			frames: this.frameCache.size,
		}
	}

	public setVisible(visible: boolean): void {
		if (this.visible === visible) return
		this.visible = visible
		if (visible) {
			void this.refresh()
		} else {
			// Drop the frames from the overlay and reclaim their GPU memory;
			// they reload from the SQLite image cache when re-enabled
			this.cloudsLayer.overlay.setForecastFrames([])
			this.cloudsLayer.overlay.setGlobalForecastFrames([])
			this.cloudsLayer.overlay.purgeForecast()
			for (const frame of this.frameCache.values()) {
				frame.purge()
			}
			this.frameCache.clear()
		}
	}

	override dispose(): void {
		this.unsubscribe?.()
		this.unsubscribe = null
		this.cloudsLayer.overlay.forecastEnabled = () => false
		this.cloudsLayer.overlay.setForecastFrames([])
		this.cloudsLayer.overlay.setGlobalForecastFrames([])
		for (const frame of this.frameCache.values()) {
			frame.purge()
		}
		this.frameCache.clear()
		super.dispose()
	}
}
